import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import ServiceAPI from '../../../services/serviceapi';
import tokenService from '../../../services/tokenservice';

interface ServerHealthResult {
    online: boolean;
    checando: boolean;
    ultimoCheck: Date | null;
    erro: string | null;
    verificarAgora: () => void;
}

interface UseServerHealthOptions {
    intervalo?: number;
    notificar?: boolean;
}

function useServerHealth(opts: UseServerHealthOptions = {}): ServerHealthResult {
    const { intervalo = 30_000, notificar = true } = opts;

    const [online, setOnline]           = useState(true);
    const [checando, setChecando]       = useState(false);
    const [ultimoCheck, setUltimoCheck] = useState<Date | null>(null);
    const [erro, setErro]               = useState<string | null>(null);

    const eraCaidoRef      = useRef(false);
    const redirecionandoRef = useRef(false);
    const navigate          = useNavigate();

    const redirecionar = useCallback(() => {
        if (redirecionandoRef.current) return;
        redirecionandoRef.current = true;

        toast.error('Sua sessão foi encerrada. Redirecionando para o login...', {
            autoClose: 2000,
            onClose: async () => {
                await tokenService.deleteToken();
                navigate('/login', { replace: true });
            },
        });
    }, [navigate]);

    const verificar = useCallback(async () => {
        setChecando(true);
        try {
            await ServiceAPI.execNativeService({
                url: '/health/ping',
                data: {},
                config: { ignoreToast: true },
            });

            setOnline(true);
            setErro(null);
            setUltimoCheck(new Date());
            redirecionandoRef.current = false;

            const empresaID = await tokenService.getEmpresaID();
            if (empresaID) {
                const nome = empresaID.charAt(0).toUpperCase() + empresaID.slice(1);
                document.title = `Gestão App Codesh (${nome})`;
            }

            if (eraCaidoRef.current && notificar) {
                toast.success('Conexão com o servidor restabelecida.', { autoClose: 3000 });
                eraCaidoRef.current = false;
            }
        } catch (e: any) {
            const status: number | undefined = e?.response?.status;

            if (status === 401) {
                redirecionar();
                return;
            }

            const msg: string =
                e?.response?.data?.message ?? e?.message ?? 'Servidor indisponível.';

            setOnline(false);
            setErro(msg);

            if (!eraCaidoRef.current && notificar) {
              //  toast.warning(`Sem comunicação com o servidor: ${msg}`, { autoClose: 5000 });
                eraCaidoRef.current = true;
            }
        } finally {
            setChecando(false);
        }
    }, [notificar, redirecionar]);

    useEffect(() => {
        verificar();
        const id = setInterval(verificar, intervalo);
        return () => clearInterval(id);
    }, [verificar, intervalo]);

    return { online, checando, ultimoCheck, erro, verificarAgora: verificar };
}

export default useServerHealth;